import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  CloudOff,
  Loader2,
  MessagesSquare,
  Rocket,
  Workflow,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { FLOW_STATUS_LABEL, type AgentConfigValue, type AgentDetail } from "@rn/contracts";
import {
  AiSection,
  BehaviorSection,
  ChannelsSection,
  KnowledgeSection,
  ScriptSection,
} from "@/components/agent/AgentFields";
import { AgentTestChat } from "@/components/agent/AgentTestChat";
import { PublishPanel } from "@/components/agent/PublishPanel";
import { useAgentAutosave, type AgentSaveState } from "@/components/agent/useAgentAutosave";
import { SectionLabel } from "@/components/flows/fields";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import { agentError, useAgent, useCompanyNumbers, usePublishAgent } from "@/lib/agent";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/agente-ia/$flowId")({
  component: AgentePage,
});

const TOM_STATUS: Record<AgentDetail["flow"]["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

type Passo = "comportamento" | "roteiro" | "conhecimento" | "canais" | "testar" | "publicar";

const PASSOS: { id: Passo; numero?: number; titulo: string; texto: string }[] = [
  { id: "comportamento", numero: 1, titulo: "Comportamento", texto: "Quem ele é e como fala" },
  { id: "roteiro", numero: 2, titulo: "Roteiro", texto: "O que descobre e o objetivo" },
  { id: "conhecimento", numero: 3, titulo: "Conhecimento", texto: "Base e modelo de IA" },
  { id: "canais", numero: 4, titulo: "Canais", texto: "WhatsApp e site" },
  { id: "testar", titulo: "Testar", texto: "Converse antes de publicar" },
  { id: "publicar", titulo: "Publicar", texto: "Checklist e códigos" },
];

function AgentePage() {
  const { flowId } = Route.useParams();
  const agente = useAgent(flowId);

  if (agente.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
          <Skeleton className="size-8" />
          <Skeleton className="h-6 w-56" />
          <Skeleton className="ml-auto h-9 w-28" />
        </div>
        <Skeleton className="m-4 min-h-0 flex-1 rounded-2xl" />
      </div>
    );
  }

  if (agente.isError || !agente.data) {
    const naoExiste = agente.error instanceof ApiError && agente.error.status === 404;
    return (
      <EmptyState
        icon={AlertCircle}
        title={naoExiste ? "Agente não encontrado" : "Não foi possível abrir o agente"}
        description={
          naoExiste
            ? "Ele pode ter sido excluído, não ser um agente (abra pelo Typechat) ou ser de outra empresa."
            : agentError(agente.error, "Confira a conexão e tente de novo.")
        }
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/agente-ia">
              <ArrowLeft />
              Voltar para a lista
            </Link>
          </Button>
        }
      />
    );
  }

  return <Editor key={flowId} initial={agente.data} />;
}

/**
 * A tela do agente: passos numerados à esquerda, o passo aberto no meio e o
 * chat de teste à direita (no celular, o teste é um passo como os outros).
 *
 * A configuração mora em estado local e salva sozinha; o detalhe que volta
 * de cada PUT traz o checklist, o "editado à mão" e o status da publicação.
 */
function Editor({ initial }: { initial: AgentDetail }) {
  const flowId = initial.flow.id;
  const live = useAgent(flowId).data ?? initial;
  const [config, setConfig] = useState<AgentConfigValue>(initial.config);
  const [passo, setPasso] = useState<Passo>("comportamento");
  const { estado, queue, flush, overwrite } = useAgentAutosave(flowId);
  const publicar = usePublishAgent(flowId);
  const numeros = useCompanyNumbers();

  const mudar = (c: AgentConfigValue) => {
    setConfig(c);
    queue(c);
  };

  const salvando = estado === "pending" || estado === "saving";
  const conflito = estado === "conflict" || live.editedByHand;
  const sujo = salvando || estado === "error" || estado === "conflict";

  const executarPublicacao = async () => {
    await flush();
    try {
      const r = await publicar.mutateAsync();
      toast.success(`Agente no ar — versão ${r.flow.publishedVersion ?? ""}`.trim());
    } catch (e) {
      toast.error(agentError(e, "Não foi possível publicar"));
    }
  };

  const bloqueios = live.checklist.filter((i) => i.blocking && !i.ok).length;
  const publicado = live.flow.publishedVersion !== null;

  const conteudo: Record<Passo, ReactNode> = {
    comportamento: (
      <BehaviorSection value={config} onChange={mudar} businessHours={live.businessHours} />
    ),
    roteiro: <ScriptSection value={config} onChange={mudar} />,
    conhecimento: (
      <div className="space-y-8">
        <KnowledgeSection value={config} onChange={mudar} />
        <section className="space-y-3">
          <SectionLabel>Modelo de IA</SectionLabel>
          <AiSection value={config} onChange={mudar} ai={live.ai} />
        </section>
      </div>
    ),
    canais: (
      <ChannelsSection
        value={config}
        onChange={mudar}
        numbers={numeros.data ?? []}
        numbersLoading={numeros.isPending}
      />
    ),
    testar: (
      <AgentTestChat
        agentId={flowId}
        welcome={config.behavior.welcome}
        saving={salvando}
        className="h-[min(70vh,40rem)] overflow-hidden rounded-xl border bg-card"
      />
    ),
    publicar: (
      <PublishPanel
        agent={live}
        dirty={sujo}
        publishing={publicar.isPending}
        onPublish={() => void executarPublicacao()}
      />
    ),
  };
  const atual = PASSOS.find((p) => p.id === passo)!;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Hint label="Voltar para a lista">
            <Button asChild size="icon-sm" variant="ghost" aria-label="Voltar para a lista">
              <Link to="/agente-ia">
                <ArrowLeft />
              </Link>
            </Button>
          </Hint>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <h1 className="min-w-0 truncate font-display text-base font-semibold tracking-tight sm:text-lg">
              {config.behavior.name.trim() || "Agente sem nome"}
            </h1>
            <Badge variant={TOM_STATUS[live.flow.status]}>
              {FLOW_STATUS_LABEL[live.flow.status]}
              {live.flow.publishedVersion ? ` · v${live.flow.publishedVersion}` : ""}
            </Badge>
            {publicado && (live.flow.hasUnpublishedChanges || sujo) && (
              <Badge variant="warning" className="hidden md:inline-flex">
                alterações não publicadas
              </Badge>
            )}
            <EstadoSalvo estado={estado} onRetry={() => void flush()} />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button asChild size="sm" variant="outline">
              <Link to="/typechat/$flowId" params={{ flowId }}>
                <Workflow />
                <span className="hidden sm:inline">Editor avançado</span>
              </Link>
            </Button>
            <Hint
              label={
                bloqueios > 0
                  ? "Resolva o checklist do passo Publicar"
                  : "Abre o checklist e publica"
              }
            >
              <Button size="sm" onClick={() => setPasso("publicar")}>
                {publicar.isPending ? <Loader2 className="animate-spin" /> : <Rocket />}
                Publicar
                {bloqueios > 0 && (
                  <span className="rounded-full bg-primary-foreground/20 px-1.5 text-[10px] tabular-nums">
                    {bloqueios}
                  </span>
                )}
              </Button>
            </Hint>
          </div>
        </div>

        {/* Passos no celular: trilho horizontal. */}
        <nav
          aria-label="Passos do agente"
          className="-mx-1 mt-2 flex gap-1 overflow-x-auto lg:hidden"
        >
          {PASSOS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPasso(p.id)}
              aria-current={passo === p.id ? "step" : undefined}
              className={cn(
                "inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors",
                passo === p.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted hover:bg-muted/70",
              )}
            >
              {p.numero && <span className="tabular-nums">{p.numero}</span>}
              {p.titulo}
            </button>
          ))}
        </nav>
      </div>

      {conflito && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-warning/18 px-4 py-2 text-xs text-warning-foreground sm:px-6 dark:text-warning">
          <AlertTriangle className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            O roteiro foi editado no editor avançado. Salvar aqui substitui essas mudanças pelo que
            está nesta tela.
          </span>
          <Button size="xs" variant="outline" asChild>
            <Link to="/typechat/$flowId" params={{ flowId }}>
              Ver no editor avançado
            </Link>
          </Button>
          <Button
            size="xs"
            variant="destructive"
            onClick={() => {
              // Sem nada na fila (só abriu a tela), manda a configuração atual.
              queue(config);
              void overwrite();
            }}
          >
            Sobrescrever com esta tela
          </Button>
        </div>
      )}

      {/* ── Corpo ─────────────────────────────────────────────────────── */}
      <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] lg:grid-cols-[13rem_minmax(0,1fr)_minmax(20rem,24rem)]">
        <nav
          aria-label="Passos do agente"
          className="hidden min-h-0 overflow-y-auto border-r bg-muted/20 p-3 lg:block"
        >
          <ol className="space-y-1">
            {PASSOS.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setPasso(p.id)}
                  aria-current={passo === p.id ? "step" : undefined}
                  className={cn(
                    "flex w-full cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                    passo === p.id ? "bg-card shadow-card" : "hover:bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                      passo === p.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {p.numero ??
                      (p.id === "testar" ? (
                        <MessagesSquare className="size-3" />
                      ) : (
                        <Rocket className="size-3" />
                      ))}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{p.titulo}</span>
                    <span className="block text-[11px] text-muted-foreground">{p.texto}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="min-h-0 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="mx-auto max-w-2xl space-y-4">
            <div>
              <h2 className="text-base font-semibold">
                {atual.numero ? `${atual.numero}. ` : ""}
                {atual.titulo}
              </h2>
              <p className="text-xs text-muted-foreground">{atual.texto}</p>
            </div>
            {/* No computador o teste já está à direita; aqui ele só aparece no celular. */}
            {passo === "testar" ? (
              <>
                <div className="lg:hidden">{conteudo.testar}</div>
                <p className="hidden text-sm text-muted-foreground lg:block">
                  Use o painel “Conversar com o agente” à direita. Ele testa o rascunho salvo — sem
                  enviar nada a ninguém.
                </p>
              </>
            ) : (
              conteudo[passo]
            )}
          </div>
        </div>

        <aside className="hidden min-h-0 border-l lg:flex lg:flex-col">
          <AgentTestChat
            agentId={flowId}
            welcome={config.behavior.welcome}
            saving={salvando}
            className="min-h-0 flex-1"
          />
        </aside>
      </div>
    </div>
  );
}

/** "Salvando…", "Salvo", "Não salvo — tentar de novo" ou o aviso de conflito. */
function EstadoSalvo({ estado, onRetry }: { estado: AgentSaveState; onRetry: () => void }) {
  if (estado === "idle") return null;
  if (estado === "conflict") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-warning-foreground dark:text-warning">
        <AlertTriangle className="size-3.5" />
        <span className="hidden sm:inline">Não salvo — editado no avançado</span>
      </span>
    );
  }
  if (estado === "error") {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs font-medium text-destructive hover:underline"
      >
        <CloudOff className="size-3.5" />
        <span className="hidden sm:inline">Não salvo — tentar de novo</span>
      </button>
    );
  }
  const salvando = estado === "pending" || estado === "saving";
  return (
    <span
      aria-live="polite"
      className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
    >
      {salvando ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
      <span className="hidden sm:inline">{salvando ? "Salvando…" : "Salvo"}</span>
    </span>
  );
}
